import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-hot-toast";

import {
  ChevronDoubleRightIcon,
  MinusSmallIcon,
} from "@heroicons/react/24/outline";

import PageHeader from "src/common/PageHeader";
import PageBody from "src/common/PageBody";
import SkuMapDeleteButton from "./SkuMapDeleteButton";
import AddWmsItemButton from "../items/AddWmsItemButton";
import ErrorAlert from "src/common/ErrorAlert";
import DisplaySkuMaps from "../items/DisplaySkuMaps";
import DisplayMappedCartItem from "./DisplayMappedCartItem";
import DisplayMappedWmsItem from "./DisplayMappedWmsItem";
import ButtonGroup from "src/common/buttons/ButtonGroup";
import Button from "src/common/buttons/Button";
import ButtonRound from "src/common/buttons/ButtonRound";
import LoadingCard from "src/common/LoadingCard";

import { apiInstance } from "src/services/api";
import { useAccount } from "src/AccountContext";
import { parseAccountParams, parseAxiosErrors } from "src/utils/Parse";
import useGetOne from "src/services/useGetOne";
import { SkuMap } from "src/services/webservice";
import { ItemType } from "../items/types";

export default function EditSkuMapPage() {
  const navigate = useNavigate();
  const { account } = useAccount();
  const params = parseAccountParams(account);
  const { integrationConfigId, wmsConnectionId, skuMapId } = useParams();
  const { data, loading, error } = useGetOne<SkuMap>(
    `/integrations/${integrationConfigId}/SkuMaps/${skuMapId}`
  );
  const [wmsItems, setWmsItems] = useState<ItemType[]>([]);

  const reMapItems = () => {
    if (data?.items && data.items.length > 0) {
      const newData: any = data.items.map((item) => {
        return {
          wmsSku: item.wmsSku,
          quantity: item.quantity,
        };
      });
      setWmsItems(newData);
    }
  };

  useEffect(() => {
    if (data?.items && data.items.length > 0) {
      const newData: any = data.items.map((item) => {
        return {
          wmsSku: item.wmsSku,
          quantity: item.quantity,
        };
      });
      setWmsItems(newData);
    }
  }, [data]);

  const addItem = (newItem: ItemType) => {
    let isNew = true;
    let newItems = wmsItems.map((item) => {
      if (item.wmsSku === newItem.wmsSku) {
        isNew = false;
        return {
          wmsSku: item.wmsSku,
          quantity: item.quantity + newItem.quantity,
        };
      } else {
        return item;
      }
    });
    if (isNew) newItems.push(newItem);
    setWmsItems(newItems);
  };

  const removeItem = (sku: string) => {
    const newItems = wmsItems.filter((item) => item.wmsSku !== sku);
    setWmsItems(newItems);
  };

  const updateMap = () => {
    toast
      .promise(
        apiInstance.put(
          `/integrations/${integrationConfigId}/SkuMaps/${skuMapId}`,
          {
            items: wmsItems,
          },
          { params }
        ),
        {
          loading: "Updating...",
          success: "Map has been updated",
          error: (err) => parseAxiosErrors(err),
        }
      )
      .then(() =>
        navigate(
          `/integrations/${integrationConfigId}/${wmsConnectionId}/items`
        )
      );
  };

  if (error)
    return (
      <div className="py-2 sm:py-10">
        <PageHeader title="Edit SKU Map" />
        <ErrorAlert error={error} />
      </div>
    );

  return (
    <div className="py-2 sm:py-10">
      <PageHeader title="Edit SKU Map" />
      <PageBody>
        {loading ? (
          <LoadingCard />
        ) : (
          <>
            <div className="flex space-y-4 sm:space-y-0 sm:space-x-10 flex-wrap">
              <div id="existing-map">
                <div className="text-lg mb-2">Existing Map:</div>
                <DisplayMappedCartItem sku={data?.cartSku} />
                <DisplaySkuMaps map={data} />
              </div>
              <div className="place-self-center invisible sm:visible">
                <ChevronDoubleRightIcon className="h-10 w-10 text-gray-400" />
              </div>
              <div className="place-self-start" id="revised-map">
                <div className="text-lg mb-2">Revised Map:</div>
                <DisplayMappedCartItem sku={data?.cartSku} />
                {wmsItems.length === 0 ? (
                  <div className="text-sm mt-2">
                    All items removed.{" "}
                    <button
                      onClick={reMapItems}
                      className="underline text-blue-700 hover:text-blue-600"
                    >
                      Click here to clear your edits.
                    </button>
                  </div>
                ) : (
                  wmsItems.map((item) => (
                    <div key={item.wmsSku} className="flex items-center">
                      <DisplayMappedWmsItem item={item} />
                      <ButtonRound
                        func={() => removeItem(item.wmsSku)}
                        icon={
                          <MinusSmallIcon
                            className="h-3 w-3"
                            aria-hidden="true"
                          />
                        }
                        label={`remove SKU ${item.wmsSku}`}
                        design="tertiary"
                        custom="ml-2 mt-2"
                      />
                    </div>
                  ))
                )}
                <div className="mt-4">
                  <AddWmsItemButton
                    wmsConnectionId={Number(wmsConnectionId)}
                    addItem={addItem}
                    params={params}
                  />
                </div>
              </div>
            </div>
            <ButtonGroup>
              <Button
                label="Cancel"
                func={() => navigate(-1)}
                design="secondary"
              />
              <SkuMapDeleteButton
                id={Number(skuMapId)}
                integrationConfigId={Number(integrationConfigId)}
                wmsConnectionId={wmsConnectionId}
              />
              <Button
                label="Save Changes"
                disabled={wmsItems.length === 0}
                func={updateMap}
              />
            </ButtonGroup>
          </>
        )}
      </PageBody>
    </div>
  );
}
